/**
 * The public API's spec as the cabinet reads it (MK42-6385): GET /api/client/api-docs/openapi.json.
 * Only what the page renders is typed; the rest of OpenAPI 3.1 passes through untouched.
 */

export type Schema = {
  $ref?: string;
  type?: string | string[];
  title?: string;
  description?: string;
  enum?: (string | number | boolean | null)[];
  items?: Schema;
  properties?: Record<string, Schema>;
  required?: string[];
  anyOf?: Schema[];
  oneOf?: Schema[];
  allOf?: Schema[];
  format?: string;
  default?: unknown;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  additionalProperties?: boolean | Schema;
};

export type Parameter = { name: string; in: 'path' | 'query' | 'header' | 'cookie'; required?: boolean; description?: string; schema?: Schema };

export type Operation = {
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: Parameter[];
  requestBody?: { required?: boolean; content?: Record<string, { schema?: Schema }> };
  responses?: Record<string, { description?: string; content?: Record<string, { schema?: Schema }> }>;
  'x-scopes'?: string[];
};

export type OpenApiSpec = {
  openapi: string;
  info: { title: string; version: string };
  servers?: { url: string; description?: string }[];
  paths: Record<string, Partial<Record<Method, Operation>>>;
  components?: { schemas?: Record<string, Schema> };
};

export type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';
const METHODS: Method[] = ['get', 'post', 'put', 'patch', 'delete'];

export type Endpoint = { method: Method; path: string; op: Operation; id: string };

/** The groups in the cabinet's words, in the order a reader needs them. */
export const TAGS: { tag: string; label: string }[] = [
  { tag: 'key', label: 'Ключ' },
  { tag: 'money', label: 'Счета, акты и сверка' },
  { tag: 'initiatives', label: 'Идеи и запросы' },
  { tag: 'work', label: 'Проекты, задачи и отчёты' },
];

/** Russian titles and notes by "METHOD path"; anything new in the spec falls back to its own summary. */
export const OPS_RU: Record<string, { title: string; note?: string }> = {
  'GET /me': { title: 'Чей это ключ', note: 'Компания, права ключа и контур. Право не нужно — удобно для проверки подключения.' },
  'GET /invoices': { title: 'Счета', note: 'Отправленные счета, новые сверху, с плательщиком.' },
  'GET /invoices/{invoice_id}': { title: 'Счёт' },
  'GET /invoices/{invoice_id}/pdf': { title: 'PDF счёта', note: 'Файл application/pdf.' },
  'POST /invoices/{invoice_id}/transfer-reported': {
    title: 'Оплачено переводом',
    note: 'Заявление клиента, как кнопка в кабинете: счёт переходит в «processing», оплату подтверждает студия по выписке. Повтор ничего не меняет (reportedNow: false). Оплаченный или отменённый счёт — 409.',
  },
  'GET /acts': { title: 'Акты', note: 'Отправленные студией акты (на подпись и подписанные). Черновики не видны.' },
  'GET /acts/{act_id}': { title: 'Акт' },
  'GET /acts/{act_id}/file': { title: 'Файл акта' },
  'GET /statement': { title: 'Акт сверки', note: 'Счета (дебет) и оплаты (кредит) по порядку и итоговый долг. Только в российском контуре и для ключа без привязки к проекту.' },
  'GET /initiatives': { title: 'Идеи и запросы', note: 'Новые сверху, со статусом как в кабинете. Суммы оценок — только ключу с read:money.' },
  'POST /initiatives': { title: 'Новая идея или запрос', note: 'Как форма в кабинете: студия получает её во входящих. externalId — ваш id (сделка amoCRM, задача Jira): повтор с тем же externalId вернёт уже созданную (200).' },
  'GET /initiatives/{initiative_id}': { title: 'Идея или запрос' },
  'POST /initiatives/{initiative_id}/answer': { title: 'Ответ на вопрос студии', note: 'Только когда статус — вопрос к вам.' },
  'GET /projects': { title: 'Проекты', note: 'Ключ с привязкой к проекту видит только его.' },
  'GET /projects/{project_id}': { title: 'Проект' },
  'GET /tasks': { title: 'Задачи', note: 'Из кеша не старше минуты: опрашивайте не чаще раза в 5 минут.' },
  'GET /tasks/{task_id}': { title: 'Задача' },
  'GET /reports': { title: 'Отчёты', note: 'Опубликованные, новые сверху.' },
  'GET /reports/{report_id}': { title: 'Отчёт' },
  'GET /reports/{report_id}/pdf': { title: 'PDF отчёта' },
  'GET /actions': { title: '«Ждут вас»', note: 'Что людям компании сделать в кабинете. Денежные пункты — только ключу с read:money.' },
};

export const opKey = (e: Pick<Endpoint, 'method' | 'path'>) => `${e.method.toUpperCase()} ${e.path}`;

/** Every operation, grouped by tag in TAGS order; an operation without a tag goes to «Ключ». */
export function groups(spec: OpenApiSpec): { tag: string; label: string; endpoints: Endpoint[] }[] {
  const all: Endpoint[] = [];
  for (const [path, ops] of Object.entries(spec.paths)) {
    for (const method of METHODS) {
      const op = ops[method];
      if (op) all.push({ method, path, op, id: `${method}-${path}`.replace(/[^a-z0-9]+/gi, '-').replace(/-+$/, '') });
    }
  }
  const tagOf = (e: Endpoint) => e.op.tags?.[0] ?? 'key';
  const known = TAGS.map((t) => t.tag);
  const extra = [...new Set(all.map(tagOf).filter((t) => !known.includes(t)))].map((tag) => ({ tag, label: tag }));
  return [...TAGS, ...extra]
    .map((t) => ({ ...t, endpoints: all.filter((e) => tagOf(e) === t.tag).sort((a, b) => a.path.localeCompare(b.path) || METHODS.indexOf(a.method) - METHODS.indexOf(b.method)) }))
    .filter((g) => g.endpoints.length > 0);
}

export const refName = (ref: string) => ref.split('/').pop() ?? ref;

export function resolve(spec: OpenApiSpec, s: Schema | undefined): Schema | undefined {
  if (!s?.$ref) return s;
  return spec.components?.schemas?.[refName(s.$ref)];
}

/** A nullable `anyOf: [X, null]` is X, optional. */
function unNull(s: Schema): { s: Schema; nullable: boolean } {
  const alts = s.anyOf ?? s.oneOf;
  if (alts) {
    const rest = alts.filter((x) => x.type !== 'null');
    if (rest.length === 1 && rest.length < alts.length) return { s: rest[0], nullable: true };
  }
  return { s, nullable: false };
}

/** A one-line type: `string`, `Money`, `InvoiceOut[]`, `"sent" | "paid"`, `string | null`. */
export function typeLabel(schema: Schema | undefined): string {
  if (!schema) return '—';
  const { s, nullable } = unNull(schema);
  const base = (() => {
    if (s.$ref) return refName(s.$ref);
    if (s.enum) return s.enum.map((v) => JSON.stringify(v)).join(' | ');
    if (s.type === 'array') return `${typeLabel(s.items)}[]`;
    const alts = s.anyOf ?? s.oneOf;
    if (alts) return alts.map(typeLabel).join(' | ');
    if (Array.isArray(s.type)) return s.type.join(' | ');
    return s.format ? `${s.type ?? 'any'} (${s.format})` : (s.type ?? 'any');
  })();
  return nullable ? `${base} | null` : base;
}

/** The schema names a schema points at (directly, through arrays or anyOf), for links. */
export function refsOf(schema: Schema | undefined): string[] {
  if (!schema) return [];
  if (schema.$ref) return [refName(schema.$ref)];
  return [schema.items, ...(schema.anyOf ?? []), ...(schema.oneOf ?? []), ...(schema.allOf ?? [])].flatMap(refsOf);
}

/** Schemas the endpoints use, reachable through properties: what the «Схемы» list shows. */
export function usedSchemas(spec: OpenApiSpec): string[] {
  const all = spec.components?.schemas ?? {};
  const seen = new Set<string>();
  const visit = (s: Schema | undefined) => {
    for (const name of refsOf(s)) {
      if (seen.has(name) || !all[name]) continue;
      seen.add(name);
      Object.values(all[name].properties ?? {}).forEach(visit);
    }
  };
  for (const ops of Object.values(spec.paths)) {
    for (const op of Object.values(ops)) {
      if (!op) continue;
      Object.values(op.requestBody?.content ?? {}).forEach((c) => visit(c.schema));
      Object.values(op.responses ?? {}).forEach((r) => Object.values(r.content ?? {}).forEach((c) => visit(c.schema)));
    }
  }
  // Validation errors are FastAPI's own: the API answers problem+json instead (see «Ошибки»).
  return [...seen].filter((n) => n !== 'HTTPValidationError' && n !== 'ValidationError').sort();
}

/** The base URL of the API: the contour's from the spec, else this site's. */
export function baseUrl(spec: OpenApiSpec | undefined, origin = typeof window !== 'undefined' ? window.location.origin : ''): string {
  return spec?.servers?.[0]?.url ?? `${origin}/api/v1`;
}
